import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
`;

const LazyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
  opacity: ${props => props.loaded ? 1 : 0};
`;

const SkeletonLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(190, 190, 190, 0.2) 25%,
    rgba(129, 129, 129, 0.24) 37%,
    rgba(190, 190, 190, 0.2) 63%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
  border-radius: 8px;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

// Brand-styled fallback card shown when a game image fails to load.
const BRAND_LOGO = '/assets/logo.png';

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  text-align: center;
  /* low-fade card gradient */
  background: linear-gradient(160deg, #232842 0%, #161a2b 45%, #0c0e18 100%);

  &::before {
    /* subtle brand glow */
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(120% 80% at 50% 0%, rgba(255, 196, 0, 0.12) 0%, rgba(255, 92, 92, 0.05) 35%, transparent 70%);
    pointer-events: none;
  }
`;

const FallbackLogo = styled.img`
  width: 70%;
  max-width: 110px;
  height: auto;
  opacity: 0.9;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5));
  z-index: 1;
`;

const FallbackLabel = styled.div`
  z-index: 1;
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
`;

// Image cache to store loaded images
const imageCache = new Map();
const loadedImages = new Set();

// Preload images in batches
const preloadImages = (urls, batchSize = 5) => {
  const batches = [];
  for (let i = 0; i < urls.length; i += batchSize) {
    batches.push(urls.slice(i, i + batchSize));
  }
  
  batches.forEach((batch, index) => {
    setTimeout(() => {
      batch.forEach(url => {
        if (!loadedImages.has(url)) {
          const img = new Image();
          img.onload = () => {
            loadedImages.add(url);
            imageCache.set(url, img);
          };
          img.src = url;
        }
      });
    }, index * 100); // Stagger batches by 100ms
  });
};

const LazyImageComponent = ({
  src,
  alt,
  className,
  onLoad,
  onError,
  placeholder,
  fallbackLabel,
  threshold = 0.1,
  rootMargin = '50px',
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Check if image is already cached
  const isCached = imageCache.has(src) || loadedImages.has(src);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [threshold, rootMargin]);

  // Load image when in view or cached
  useEffect(() => {
    if (!inView && !isCached) return;

    const img = new Image();
    
    img.onload = () => {
      setLoaded(true);
      setError(false);
      imageCache.set(src, img);
      loadedImages.add(src);
      onLoad?.();
    };
    
    img.onerror = () => {
      setError(true);
      setLoaded(false);
      onError?.();
    };
    
    img.src = src;
  }, [inView, src, isCached, onLoad, onError]);

  // If cached, load immediately
  useEffect(() => {
    if (isCached) {
      setLoaded(true);
      onLoad?.();
    }
  }, [isCached, onLoad]);

  return (
    <ImageContainer ref={imgRef} className={className} {...props}>
      {!loaded && !error && <SkeletonLoader />}
      {error && (
        <ErrorPlaceholder>
          <FallbackLogo src={BRAND_LOGO} alt={alt || 'game'} />
          {fallbackLabel && <FallbackLabel>{fallbackLabel}</FallbackLabel>}
        </ErrorPlaceholder>
      )}
      {loaded && (
        <LazyImage
          src={src}
          alt={alt}
          loaded={loaded}
          onLoad={() => setLoaded(true)}
        />
      )}
    </ImageContainer>
  );
};

// Export preload function for external use
export { preloadImages };
export default LazyImageComponent;
